import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '@/shared/lib/utils';
import { Icon, type PhxIconName } from '@/shared/ui/icon';
import { FIELD_FOCUS_RING, FIELD_REQUIRED_BG } from '@/shared/ui/fieldStyles';

export type InputSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASSES: Record<InputSize, string> = {
  xs: 'h-phx-control-xs text-phx-xs leading-[20px] px-phx-2',
  sm: 'h-phx-control-sm text-phx-xs leading-[22px] px-phx-3',
  md: 'h-phx-control text-phx-md leading-[26px] px-phx-3_5',
  lg: 'h-phx-control-lg text-phx-md leading-[28px] px-phx-3',
  xl: 'h-phx-control-xl text-phx-lg leading-[32px] px-phx-5',
};

const BASE_INPUT =
  'phx-input block w-full bg-phx-surface text-phx-text rounded-phx ' +
  'border border-phx-border-2 transition-[border-color,box-shadow,background-color] ' +
  'placeholder:text-phx-text-faint ' +
  'hover:border-phx-border-3 ' +
  'disabled:bg-phx-surface-3 disabled:text-phx-text-muted disabled:cursor-not-allowed';

const DRAFT_BG = 'bg-phx-draft-tint';
const INVALID_RING =
  'border-phx-danger focus:border-phx-danger focus:shadow-[0_0_0_2px_rgba(181,52,42,0.25)]';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize;
  invalid?: boolean;
  draft?: boolean;
  leadingIcon?: PhxIconName;
  trailingIcon?: PhxIconName;
  /** Jednotka zobrazená uvnitř pole vpravo (např. „min", „km") — muted, neklikatelná. */
  unit?: string;
}

/**
 * Phoenix text input.  Supports the four control heights, semantic states
 * (required/invalid/draft) and optional leading/trailing icons.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', invalid, draft, required, leadingIcon, trailingIcon, unit, className, ...rest },
  ref
) {
  const inputClasses = cn(
    BASE_INPUT,
    FIELD_FOCUS_RING,
    SIZE_CLASSES[size],
    required && FIELD_REQUIRED_BG,
    draft && DRAFT_BG,
    invalid && INVALID_RING,
    leadingIcon && 'pl-[26px]',
    trailingIcon && 'pr-[26px]',
    unit && 'pr-9',
    className
  );

  if (leadingIcon || trailingIcon || unit) {
    return (
      <div className="phx-input-wrap relative flex items-center">
        {leadingIcon ? (
          <Icon
            name={leadingIcon}
            size={13}
            className="text-phx-text-muted left-phx-4 pointer-events-none absolute"
          />
        ) : null}
        <input
          ref={ref}
          data-size={size}
          data-invalid={invalid ? '' : undefined}
          data-draft={draft ? '' : undefined}
          required={required}
          className={inputClasses}
          {...rest}
        />
        {trailingIcon ? (
          <Icon
            name={trailingIcon}
            size={13}
            className="text-phx-text-muted right-phx-4 pointer-events-none absolute"
          />
        ) : null}
        {unit ? (
          <span className="text-phx-text-muted text-phx-xs right-phx-3 pointer-events-none absolute select-none">
            {unit}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <input
      ref={ref}
      data-size={size}
      data-invalid={invalid ? '' : undefined}
      data-draft={draft ? '' : undefined}
      required={required}
      className={inputClasses}
      {...rest}
    />
  );
});
